import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { ChannelSourceService } from './channel-source/channel-source.service';
import { CHANNEL_SOURCE } from './channel-source/channel-source.model';
import { LocalStorageService } from './storage/local-storage.service';
import { LocalStorageVariables } from '../enum/localstorage-variables';
import { isPlatformBrowser } from '@angular/common';

export interface UTMData {
  utm_source: string;
  utm_medium: string;
  channel: string;
  slug: string;
  timestamp: number; // when it was saved
}

export interface UtmTransferPayload {
  utm_source: string;
  utm_medium: string;
}
@Injectable({
  providedIn: 'root'
})
export class UtmService {
  private channelSourceService = inject(ChannelSourceService);
  private localStorageService = inject(LocalStorageService);
  private platformId = inject(PLATFORM_ID);
  private EXPIRATION_DAYS = 7;

  private channelMap: Record<string, { slug: CHANNEL_SOURCE; medium: string }> =
    {
      twitter: { slug: CHANNEL_SOURCE.KingTwitter, medium: 'social' },
      google: { slug: CHANNEL_SOURCE.KingGoogleAds, medium: 'cpc' },
      facebook: { slug: CHANNEL_SOURCE.KingFacebook, medium: 'social' }
    };

  //this method should be called on app initialization
  // it checks for utm parameters in the URL and saves them if found
  // If no UTM parameters are found, it checks for existing UTM data in local storage
  // and verifies if it's still valid (not expired)
  // If valid UTM data exists, it sets the channel source accordingly
  // If no valid UTM data exists, it defaults the channel source based on device type
  //sample URL with utm data: http://localhost:4200/?utm_source=twitter&utm_medium=social

  captureUTMFromUrl(): void {
    if (!isPlatformBrowser(this.platformId) || typeof window === 'undefined') {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const source = (urlParams.get('utm_source') ?? '').trim().toLowerCase();
    const medium = (urlParams.get('utm_medium') ?? '').trim().toLowerCase();

    if (source || medium) {
      // New UTM data from URL always replaces previous stored value.
      this.saveUTM(source, medium);
      this.applyChannelFromUtmData(this.getUTM());
      return;
    }

    this.checkIfExistingUTMExpired();
  }

  applyUtmFromTransferState(payload: UtmTransferPayload): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const source = (payload.utm_source ?? '').trim().toLowerCase();
    const medium = (payload.utm_medium ?? '').trim().toLowerCase();
    this.saveUTM(source, medium);
    this.checkIfExistingUTMExpired();
  }

  checkIfExistingUTMExpired(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const utmData = this.getUTM();
    if (!utmData) {
      this.channelSourceService.setDefaultSource();
      return;
    }

    const diffDays = (Date.now() - utmData.timestamp) / (1000 * 60 * 60 * 24);
    if (diffDays > this.EXPIRATION_DAYS) {
      this.localStorageService.deleteItem(LocalStorageVariables['utm-data']);
      this.channelSourceService.setDefaultSource();
    } else {
      this.applyChannelFromUtmData(utmData);
    }
  }

  private saveUTM(source: string, medium: string) {
    const channelInfo = this.channelMap[source];

    const newData: UTMData = {
      utm_source: source,
      utm_medium: medium,
      channel: channelInfo
        ? `${source} / ${channelInfo.medium}`
        : `${source} / ${medium}`,
      slug: channelInfo ? channelInfo.slug : '',
      timestamp: Date.now()
    };

    this.localStorageService.setItem(
      LocalStorageVariables['utm-data'],
      JSON.stringify(newData)
    );
  }

  private applyChannelFromUtmData(utmData: UTMData | null): void {
    if (!utmData) {
      this.channelSourceService.setDefaultSource();
      return;
    }

    const detectedChannel = this.detectChannelFromUtmData(utmData);
    if (detectedChannel) {
      this.channelSourceService.channelSource = detectedChannel;
      return;
    }

    this.channelSourceService.setDefaultSource();
  }

  private detectChannelFromUtmData(utmData: UTMData): CHANNEL_SOURCE | null {
    const candidates = [utmData.utm_source, utmData.utm_medium, utmData.channel]
      .filter((value): value is string => !!value)
      .map((value) => value.toLowerCase());

    for (const value of candidates) {
      if (value.includes('binance') || value === 'bn') {
        return CHANNEL_SOURCE.BINANCE;
      }

      if (
        value.includes('gate') ||
        value.includes('gateio') ||
        value.includes('gate-io')
      ) {
        return CHANNEL_SOURCE.GATEIO;
      }

      if (value.includes('kucoin')) {
        return CHANNEL_SOURCE.KUCOIN;
      }

      if (value.includes('bybit')) {
        return CHANNEL_SOURCE.BYBIT;
      }

      for (const key of Object.keys(this.channelMap)) {
        if (value.includes(key)) {
          return this.channelMap[key].slug;
        }
      }
    }

    return null;
  }

  getUTM(): UTMData | null {
    const dataStr = this.localStorageService.getItem(
      LocalStorageVariables['utm-data']
    );
    if (!dataStr) {
      return null;
    }

    try {
      return JSON.parse(dataStr) as UTMData;
    } catch {
      this.localStorageService.deleteItem(LocalStorageVariables['utm-data']);
      return null;
    }
  }

  clearUTM(): void {
    this.localStorageService.deleteItem(LocalStorageVariables['utm-data']);
  }
}
